import { useState, useRef, useEffect } from 'react'
import {
  Send,
  Trash2,
  Loader2,
  AlertCircle,
  Bot,
  User,
  Coins,
  Square,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { usePlaygroundStore } from '@/stores/playground-store'
import type { PlaygroundMessage, ToolCallInfo } from '@/schemas/playground'

interface AgentPlaygroundProps {
  projectId: string
  agentId: string
  className?: string
}

export function AgentPlayground({
  projectId,
  agentId,
  className,
}: AgentPlaygroundProps) {
  const [inputMessage, setInputMessage] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const {
    messages,
    isLoading,
    isStreaming,
    error,
    lastUsage,
    lastModel,
    lastProvider,
    sendMessageStream,
    cancelStream,
    clearHistory,
    clearError,
  } = usePlaygroundStore()

  // Auto-scroll to bottom when new messages arrive or content updates during streaming
  const lastMessageContent = messages[messages.length - 1]?.content
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length, lastMessageContent])

  // Reset store when component unmounts or agent changes
  useEffect(() => {
    return () => {
      clearHistory()
    }
  }, [agentId, clearHistory])

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading || isStreaming) return

    const message = inputMessage.trim()
    setInputMessage('')

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }

    await sendMessageStream(projectId, agentId, message)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value)

    // Auto-resize textarea
    const textarea = e.target
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-indigo-400" />
          <h3 className="font-medium text-white">Playground</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearHistory}
          disabled={messages.length === 0 || isLoading || isStreaming}
          className="text-gray-400 hover:text-white"
        >
          <Trash2 className="h-4 w-4 mr-1" />
          Limpar
        </Button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
            <Bot className="h-12 w-12 mb-3 opacity-50" />
            <p className="text-sm">Envie uma mensagem para testar seu agente</p>
            <p className="text-xs mt-1 text-gray-600">
              O historico nao e persistido
            </p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={index}
                message={message}
                isStreaming={
                  isStreaming &&
                  index === messages.length - 1 &&
                  message.role === 'assistant'
                }
              />
            ))}
            {isLoading && !isStreaming && (
              <div className="flex items-center gap-2 text-gray-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Pensando...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Token Usage */}
      {lastUsage && (
        <div className="px-4 py-2 border-t border-gray-700/50 bg-gray-800/50">
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <div className="flex items-center gap-1">
              <Coins className="h-3 w-3" />
              <span>Tokens:</span>
            </div>
            <span title="Prompt tokens">{lastUsage.prompt_tokens}</span>
            <span className="text-gray-600">+</span>
            <span title="Completion tokens">{lastUsage.completion_tokens}</span>
            <span className="text-gray-600">=</span>
            <span className="text-indigo-400 font-medium" title="Total tokens">
              {lastUsage.total_tokens}
            </span>
            {lastProvider && lastModel && (
              <span className="ml-auto text-gray-500">
                {lastProvider}/{lastModel}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="px-4 py-2 border-t border-red-500/20 bg-red-500/10">
          <div className="flex items-start gap-2 text-sm text-red-400">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button
              onClick={clearError}
              className="text-red-400/60 hover:text-red-400"
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="p-4 border-t border-gray-700/50">
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            value={inputMessage}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="Digite sua mensagem..."
            disabled={isLoading || isStreaming}
            rows={1}
            className={cn(
              'flex-1 resize-none rounded-lg px-3 py-2',
              'bg-gray-800/50 border border-gray-700/50',
              'text-white placeholder:text-gray-500',
              'focus:outline-none focus:ring-2 focus:ring-indigo-500/50',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'text-sm'
            )}
          />
          {isStreaming ? (
            <Button
              onClick={cancelStream}
              size="sm"
              variant="ghost"
              className="self-end text-red-400 hover:text-red-300 hover:bg-red-500/10"
              title="Cancelar geracao"
            >
              <Square className="h-4 w-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSend}
              disabled={!inputMessage.trim() || isLoading}
              size="sm"
              className="self-end"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Pressione Enter para enviar, Shift+Enter para nova linha
        </p>
      </div>
    </div>
  )
}

interface MessageBubbleProps {
  message: PlaygroundMessage
  isStreaming?: boolean
}

function MessageBubble({ message, isStreaming }: MessageBubbleProps) {
  const isUser = message.role === 'user'

  return (
    <div
      className={cn('flex gap-3', isUser ? 'flex-row-reverse' : 'flex-row')}
    >
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-indigo-500/20' : 'bg-emerald-500/20'
        )}
      >
        {isUser ? (
          <User className="h-4 w-4 text-indigo-400" />
        ) : (
          <Bot className="h-4 w-4 text-emerald-400" />
        )}
      </div>
      <div
        className={cn(
          'flex-1 rounded-xl px-4 py-2 max-w-[85%]',
          isUser
            ? 'bg-indigo-500/20 text-white'
            : 'bg-gray-800/50 text-gray-200'
        )}
      >
        <p className="text-sm whitespace-pre-wrap break-words">
          {message.content}
          {isStreaming && (
            <span className="inline-block w-1.5 h-4 ml-0.5 bg-indigo-400 animate-pulse align-text-bottom" />
          )}
        </p>
        {message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCallIndicator toolCalls={message.toolCalls} />
        )}
        {message.timestamp && (
          <p className="text-xs text-gray-500 mt-1">
            {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        )}
      </div>
    </div>
  )
}

interface ToolCallIndicatorProps {
  toolCalls: ToolCallInfo[]
}

function ToolCallIndicator({ toolCalls }: ToolCallIndicatorProps) {
  return (
    <div className="flex flex-wrap gap-1 mt-2">
      {toolCalls.map((tc, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-700/50 text-gray-300"
        >
          {tc.status === 'running' ? (
            <Loader2 className="h-3 w-3 animate-spin text-indigo-400" />
          ) : tc.status === 'completed' ? (
            <Check className="h-3 w-3 text-emerald-400" />
          ) : (
            <AlertCircle className="h-3 w-3 text-red-400" />
          )}
          {tc.name}
          {tc.execution_time_ms !== undefined && tc.status !== 'running' && (
            <span className="text-gray-500">
              ({(tc.execution_time_ms / 1000).toFixed(1)}s)
            </span>
          )}
        </span>
      ))}
    </div>
  )
}
